import { PayloadAction } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import {
  all, call, put, takeLatest,
} from 'redux-saga/effects'
import { ReturnCall } from 'src/features/types'
import { actions as appActions } from '../state'
import apis from './SupplierApi'
import { actions } from './SupplierState'
import {
  GetPreQueuePayload,
  PreQueueInvoicesPayloadSagas,
  ValidateBilletBarcodePayloadSagas,
  ValidateExternalPurchaseOrderCodePayload,
} from './SupplierTypes'
import dayjs from 'dayjs'

export function* getPreQueue({ payload }: PayloadAction<GetPreQueuePayload>) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.getPreQueue> = yield call(apis.getPreQueue, payload)

    if (!result.isOk) {
      const errorMessage = result?.data?.data?.message || 'Falha ao carregar a pré fila!'
      toast.error(errorMessage)
      yield put(actions.setLastUpdatedInfo({
        dateOrMessage: errorMessage,
        error: true,
      }))
      return
    }

    const { preQueue, deletedPreQueue } = result.data

    yield put(actions.setPreQueueItems(preQueue))
    yield put(actions.setPreQueueDeletedItems(deletedPreQueue))
    yield put(actions.setLastUpdatedInfo({
      dateOrMessage: dayjs().format('HH:mm:ss'),
      error: '',
    }))
  } catch (error: any) {
    const errorMessage = 'Falha ao carregar a pré fila!'
    toast.error(errorMessage)
    yield put(actions.setLastUpdatedInfo({
      dateOrMessage: errorMessage,
      error: true,
    }))
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* releasePreQueueInvoices(data: PayloadAction<PreQueueInvoicesPayloadSagas>) {
  yield put(appActions.isLoading(true))

  const {
    badge, preQueueId, fcId, onReleaseIncompatibleInvoices, onFinally,
  } = data.payload

  try {
    const result: ReturnCall<typeof apis.releasePreQueueInvoices> = yield call(
      apis.releasePreQueueInvoices,
      { badge, preQueueId },
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao liberar Notas!')
      if (onReleaseIncompatibleInvoices && result?.data?.data?.errorCode === 'ARS_INCOMPATIBLE_TYPE' && result?.data?.data?.arsByType) {
        onReleaseIncompatibleInvoices(result?.data?.data?.arsByType)
      }
      return
    }

    if (result.data.message) {
      toast.info(result.data.message)
    } else {
      toast.success('Notas fiscais liberadas com sucesso!')
    }

    yield call(getPreQueue, { payload: { fcId }, type: '' })
  } catch (error: any) {
    toast.error('Falha ao liberar invoices!')
  } finally {
    yield put(appActions.isLoading(false))
    onFinally()
  }
}

export function* removePreQueueInvoices(data: PayloadAction<PreQueueInvoicesPayloadSagas>) {
  yield put(appActions.isLoading(true))

  const {
    badge, preQueueId, fcId, onFinally,
  } = data.payload

  try {
    const result: ReturnCall<typeof apis.removePreQueueInvoices> = yield call(
      apis.removePreQueueInvoices,
      { badge, preQueueId },
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao remover as notas fiscais!')
      return
    }

    toast.success('Notas fiscais removidas com sucesso!')

    yield call(getPreQueue, { payload: { fcId }, type: '' })
  } catch (error: any) {
    toast.error('Falha ao remover as notas fiscais!')
  } finally {
    yield put(appActions.isLoading(false))
    onFinally()
  }
}

export function* validateBilletBarcode({ payload: { barcode, onFail, onSuccess } }: PayloadAction<ValidateBilletBarcodePayloadSagas>) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.validateBilletBarcode> = yield call(
      apis.validateBilletBarcode,
      barcode,
    )

    if (!result.isOk) {
      onFail(result.data?.data?.message ?? 'Boleto inválido!')
      return
    }

    onSuccess()
  } catch (error: any) {
    onFail('Falha ao validar boleto!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* validateExternalPurchaseOrderCode(data: PayloadAction<ValidateExternalPurchaseOrderCodePayload>) {
  const {
    externalPurchaseOrderCode, onSuccess, onFail, setLoading,
  } = data.payload

  setLoading(true)

  try {
    const result: ReturnCall<typeof apis.validateExternalPurchaseOrderCode> = yield call(
      apis.validateExternalPurchaseOrderCode,
      externalPurchaseOrderCode,
    )

    if (!result.isOk) {
      onFail(result.data?.data?.message || 'Falha ao validar código de pedido de compra externa')
      return
    }

    onSuccess()
  } catch (error: any) {
    onFail('Falha ao validar código de pedido de compra externa!')
  } finally {
    setLoading(false)
  }
}

export default function* () {
  yield all([
    takeLatest(actions.getPreQueue.type, getPreQueue),
    takeLatest(actions.releasePreQueueInvoices.type, releasePreQueueInvoices),
    takeLatest(actions.removePreQueueInvoices.type, removePreQueueInvoices),
    takeLatest(actions.validateBilletBarcode.type, validateBilletBarcode),
    takeLatest(actions.validateExternalPurchaseOrderCode.type, validateExternalPurchaseOrderCode),
  ])
}
